import React, {useContext, useEffect, useRef, useState} from 'react';
import 'styles/item/Items.css';
import FilterDropdown from 'components/items/header/FilterDropdown';
import SortDropdown from 'components/items/header/SortDropdown';
import MyTradeOffers from 'components/items/popup/MyTradeOffers';
import {usePopup} from 'popup/PopupContext';
import {MarketContext} from 'util/market/MarketContext';
import {centsToString} from 'util/cashFormatter';
import NiceButton from 'components/pictures/NiceButton';
import {FilterLabels} from 'components/items/FilterLabels';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';

const MarketOptionsHeader = ({
                                 config,
                                 toggleConfig,
                                 onSearchTextChange,
                                 onSortChange
                             }) => {
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [showSortPopup, setShowSortPopup] = useState(false);
    const [searchMode, setSearchMode] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedSortField, setSelectedSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const {openPopup} = usePopup();
    const {allItems} = useContext(MarketContext);
    const [totalBuyingFor, setTotalBuyingFor] = useState(0);
    const [totalSellingFor, setTotalSellingFor] = useState(0);
    const [fakeReverseOrder, setFakeReverseOrder] = useState(false);
    const [showArrowUp, setShowArrowUp] = useState(true);
    const inputRef = useRef(null);
    const buttonRef = useRef(null); // Reference for the Sort button
    const {isDesktopView} = useSessionConfiguration();

    useEffect(() => {
        if (allItems) {
            setTotalBuyingFor(getTotalUserBuyingFor(allItems));
            setTotalSellingFor(getTotalUserSellingFor(allItems));
        }
    }, [allItems]);

    const handleSearchClick = () => {
        setSearchMode(true);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        onSearchTextChange(e.target.value);
    };

    const handleSearchBlur = () => {
        if (searchText === '') {
            setSearchMode(false);
        }
    };

    const clearSearch = () => {
        setSearchText('');
        onSearchTextChange('');
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const handleSortFieldChange = (field, order) => {
        setSelectedSortField(field);
        setSortOrder(order);
        setShowSortPopup(false);
        onSortChange(field, order);
    };

    const getTotalUserBuyingFor = (items) => {
        return items.reduce((total, item) => {
            if (item.isUserBuying()) {
                return total + item.getUserBo() * item.getUserBuyingQuantity();
            }
            return total;
        }, 0);
    };

    const getTotalUserSellingFor = (items) => {
        return items.reduce((total, item) => {
            if (item.isUserSelling()) {
                return total + item.getUserSo() * item.getUserSellingQuantity();
            }
            return total;
        }, 0);
    };

    useEffect(() => {
        const arrowUp = (sortOrder === 'asc' && !fakeReverseOrder) || (sortOrder === 'desc' && fakeReverseOrder);
        setShowArrowUp(arrowUp);
    }, [fakeReverseOrder, sortOrder]);

    const fraudMouseEnter = () => {
        setFakeReverseOrder(true);
    };

    const fraudMouseLeave = () => {
        setFakeReverseOrder(false);
    };

    const sortOrderFraudClick = () => {
        const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newOrder);
        setFakeReverseOrder(false);
        onSortChange(selectedSortField, newOrder);
    };

    const showFilterPopupClick = () => {
        setShowFilterPopup(!showFilterPopup);
        setShowSortPopup(false);
    };

    return (
        <>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: isDesktopView ? 'row' : 'column'
            }}>
                <div className={`market-right-section ${isDesktopView ? '' : 'mobile'}`}>
                    {totalBuyingFor > 0 && (
                        <NiceButton
                            onClick={() => openPopup(<MyTradeOffers isBuy={true}/>)}
                            text={'Buying ' + centsToString(totalBuyingFor)}
                            small={!isDesktopView}
                        />
                    )}
                    {totalSellingFor > 0 && (
                        <NiceButton
                            onClick={() => openPopup(<MyTradeOffers isBuy={false}/>)}
                            text={'Selling ' + centsToString(totalSellingFor)}
                            small={!isDesktopView}
                        />
                    )}
                    {searchMode ? (
                        <div className="item-search-input-foreground">
                            <input
                                type="text"
                                value={searchText}
                                onChange={handleSearchChange}
                                onBlur={handleSearchBlur}
                                placeholder="Search..."
                                className="search-one-line-input"
                                ref={inputRef}
                            />
                            {searchText && (
                                <button
                                    className="market-header-clean-search"
                                    onClick={clearSearch}
                                >
                                    &#x2715;
                                </button>
                            )}
                        </div>
                    ) : (
                        <NiceButton onClick={handleSearchClick}>
                            <img
                                src="/images/store/magnifying-glass.svg"
                                alt="Search"
                                width={'15px'}
                                style={{padding: '2px 0'}}
                            />
                        </NiceButton>
                    )}
                    <div className="market-header-popup-button-container">
                        <NiceButton onClick={showFilterPopupClick}>
                            <img
                                src="/images/store/eye.svg"
                                alt="Search"
                                width={'15px'}
                                style={{padding: '2px 0'}}
                            />
                        </NiceButton>
                        {showFilterPopup && (
                            <FilterDropdown
                                config={config}
                                toggleConfig={toggleConfig}
                                onClose={() => setShowFilterPopup(false)}
                            />
                        )}
                    </div>

                    <div className="market-header-popup-button-container">
                        <div className="position-relative">
                            {selectedSortField && (
                                <div
                                    className="sort-order-fraud"
                                    onClick={sortOrderFraudClick}
                                    onMouseEnter={fraudMouseEnter}
                                    onMouseLeave={fraudMouseLeave}
                                />
                            )}
                            <div ref={buttonRef}>
                                <NiceButton
                                    onClick={() => setShowSortPopup(!showSortPopup)}
                                    text={(FilterLabels[selectedSortField]
                                        ? (showArrowUp ? '↑ ' : '↓ ') + FilterLabels[selectedSortField].label
                                        : '↑↓')}
                                />
                            </div>
                        </div>
                        {showSortPopup && (
                            <SortDropdown
                                selectedSortField={selectedSortField}
                                sortOrder={sortOrder}
                                setSortField={handleSortFieldChange}
                                onClose={() => setShowSortPopup(false)}
                                buttonRef={buttonRef} // Pass button ref to SortDropdown
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default MarketOptionsHeader;
